import {
  IndexedParticipation,
  Participation,
  usePrefetchParticipationResponse,
} from '@app/api';
import { ParticipationDefaultCell } from './index';
import { Checkbox } from '@app/form';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useCallback, useEffect, useRef, useState } from 'react';
import difference from 'lodash/difference';
import classNames from 'classnames';
import { FieldConfig } from '../../hooks/useFieldConfig';
import { union } from 'lodash';

const ANIMATION_DURATION = 700;

interface Props {
  participations: IndexedParticipation[];
  selectedParticipations: Participation[];
  onSelectParticipation: (p: IndexedParticipation) => any;
  fieldConfigs: FieldConfig[];
  shouldAnimate?: boolean;
  hiddenColumns: string[];
}

const useStyles = createUseStyles((theme: Theme) => ({
  removing: {
    animation: '$crushOut 600ms cubic-bezier(0.4, 0, 0.2, 1) forwards',
    overflow: 'hidden',
    pointerEvents: 'none',
    fontSize: 0,
    '& td': {
      overflow: 'hidden',
      fontSize: 0,
      animation: '$crushOutCell 600ms cubic-bezier(0.4, 0, 0.2, 1) forwards',
    },
  },
  '@keyframes crushOut': {
    '0%': {
      height: theme.marginBase * 11,
      maxHeight: theme.marginBase * 11,
      opacity: 1,
      transform: 'scaleY(1)',
    },
    '30%': {
      opacity: 0.6,
    },
    '99%': {
      height: 0,
      maxHeight: 0,
      opacity: 0,
      transform: 'scaleY(0)',
      overflow: 'hidden',
      lineHeight: 0,
      fontSize: 0,
      margin: 0,
    },
    '100%': {
      height: 0,
      maxHeight: 0,
      minHeight: 0,
      opacity: 0,
      transform: 'scaleY(0)',
      overflow: 'hidden',
      lineHeight: 0,
      fontSize: 0,
      margin: 0,
      visibility: 'collapse',
    },
  },
  '@keyframes crushOutCell': {
    '0%': {
      height: theme.marginBase * 11,
      paddingTop: theme.marginBase * 2,
      paddingBottom: theme.marginBase * 2,
      borderBottomWidth: 1,
    },
    '99%': {
      height: 0,
      paddingTop: 0,
      paddingBottom: 0,
      borderBottomWidth: 0,
      lineHeight: 0,
    },
    '100%': {
      height: 0,
      paddingTop: 0,
      paddingBottom: 0,
      borderBottom: 'none',
      lineHeight: 0,
      display: 'none',
    },
  },
  checked: {
    backgroundColor: theme.colors.grayBackground,
  },
  notChecked: {
    backgroundColor: theme.colors.white,
  },
  row: {
    '&:hover .checkbox-icon': {
      color: theme.colors.blueAccessible,
    },
  },
}));

export const ParticipationTableRows = ({
  participations,
  selectedParticipations,
  onSelectParticipation,
  fieldConfigs,
  shouldAnimate = true,
  hiddenColumns,
}: Props) => {
  const classes = useStyles();

  const [displayedParticipations, setDisplayedParticipations] =
    useState(participations);

  const [animatingParticipationIds, setAnimatingParticipationIds] = useState<
    string[]
  >([]);
  const prevParticipationsRef = useRef(participations);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const prefetchParticipation = usePrefetchParticipationResponse();

  useEffect(() => {
    if (
      prevParticipationsRef.current?.length <= participations.length ||
      !shouldAnimate
    ) {
      prevParticipationsRef.current = participations;
      setDisplayedParticipations(participations);
      setAnimatingParticipationIds([]);
      return;
    }

    const prevIds = prevParticipationsRef.current.map(({ id }) => id);
    const currentIds = participations.map(({ id }) => id);
    const newlyRemoved = difference(prevIds, currentIds);

    setAnimatingParticipationIds((prev) => {
      return union(prev, newlyRemoved);
    });
    prevParticipationsRef.current = participations;

    const timeoutId = setTimeout(() => {
      setDisplayedParticipations(participations);
      setAnimatingParticipationIds([]);
    }, ANIMATION_DURATION + 100);

    return () => clearTimeout(timeoutId);
  }, [participations, shouldAnimate]);

  const clearPrefetchTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  const onHover = useCallback(
    (participation: IndexedParticipation) => {
      if (participation) {
        if (timeoutRef.current) {
          clearPrefetchTimeout();
        }

        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = undefined;
          prefetchParticipation(participation.id, participation.applicationId);
        }, 300);
      }
    },
    [clearPrefetchTimeout, prefetchParticipation],
  );

  const onLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearPrefetchTimeout();
    }
  }, [clearPrefetchTimeout]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearPrefetchTimeout();
      }
    };
  }, [clearPrefetchTimeout]);

  const isChecked = (id: string) => {
    return !!selectedParticipations.find((val) => val.id === id);
  };

  return (
    <>
      {displayedParticipations.map((p: IndexedParticipation) => {
        return (
          <tr
            key={p.id}
            className={classNames(classes.row, {
              [classes.removing]: animatingParticipationIds.includes(p.id),
              [classes.checked]: isChecked(p.id),
            })}
            onMouseEnter={() => onHover(p)}
            onMouseLeave={onLeave}
            onClick={(e) => {
              const target = e.target as HTMLElement;
              if (target.closest('[data-no-row-click]')) return;
              onSelectParticipation(p);
            }}
          >
            <td
              className={classNames({
                [classes.checked]: isChecked(p.id),
                [classes.notChecked]: !isChecked(p.id),
              })}
              data-no-row-click
            >
              <Checkbox
                checked={isChecked(p.id)}
                value={p.id}
                onChange={() => onSelectParticipation(p)}
                noMargin
              />
            </td>
            {fieldConfigs.map((config) => {
              if (!config.relevant || hiddenColumns.includes(config.field)) {
                return null;
              }

              if (config.renderCell) {
                return config.renderCell(p, config);
              }

              return (
                <ParticipationDefaultCell
                  key={config.field}
                  field={config.field}
                  type={config.type}
                  participation={p}
                />
              );
            })}
          </tr>
        );
      })}
    </>
  );
};
